import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { updateOrderAttributes } from '@theme/order-attributes';
// @ts-ignore Resolved via bundler aliases
import { API_ENDPOINTS } from '@form/constants';
// @ts-ignore Resolved via bundler aliases
import { CSS_CLASSES } from '@form/constants';
// @ts-ignore Resolved via bundler aliases
import { EVENTS, STORAGE_KEYS } from '@form/data-constants';
// @ts-ignore Resolved via bundler aliases
import { buildClubApiUrl, getStoredClubData } from '@form/utils';
import {
  toggleCheckoutButtons,
  disableCheckoutButtons,
  enableCheckoutButtons,
  formatDiscountMessage,
} from './club-validation.utils.js';
import { CLUB_VALIDATION_SELECTORS } from './club-validation.constants.js';

const CART_PAGE_CLUB_CLASS = 'cart-page--club-pharmacy-pickup';

/**
 * Combined discount / club field for the cart.
 *
 * It first checks whether the entered value is a valid club ID.
 * If not, it falls back to the normal Shopify discount flow.
 */
class CombineDiscountClubComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#restoreClubStatus();
  }

  /**
   * Called via on:submit="/validateDiscountOrClub"
   * @param {SubmitEvent} event
   */
  async validateDiscountOrClub(event) {
    event.preventDefault();
    event.stopPropagation();

    const submittedValue = this.#getInputValue();
    if (!submittedValue) return;

    this.#beginValidation();

    try {
      const clubResponse = await this.#fetchClubValidation(submittedValue);
      const clubData = clubResponse?.data || clubResponse;

      if (clubResponse?.success && clubData) {
        await this.#handleClubSuccess(clubData);
        return;
      }
    } catch (_error) {
      // Fall back to the normal discount flow below.
    }

    try {
      await this.#clearClubState();
      await this.#applyDiscountCode(submittedValue);
    } finally {
      this.#finishValidation();
    }
  }

  /**
   * Called via on:click="/removeDiscount"
   * @param {MouseEvent | KeyboardEvent} event
   */
  async removeDiscount(event) {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter')
      || !(event.target instanceof HTMLElement)
    ) {
      return;
    }

    const pill = event.target.closest('[data-discount-code]');
    const discountCode = pill?.getAttribute('data-discount-code');

    if (!discountCode) return;

    await this.#removeDiscountCode(discountCode);
  }

  /**
   * Called via on:click="/removeClubId"
   * @param {MouseEvent | KeyboardEvent} event
   */
  async removeClubId(event) {
    event.preventDefault();
    event.stopPropagation();

    this.#clearInput();
    await this.#clearClubState();
  }

  /**
   * @param {any} clubData
   */
  async #handleClubSuccess(clubData) {
    this.#hideError();
    this.#openDisclosure();
    this.#showClubSuccess(clubData);
    await this.#applyClubState(clubData);
    this.dispatchEvent(new CustomEvent('combine-discount-club:club-success', {
      detail: { clubData },
      bubbles: true,
    }));
  }

  /**
   * @param {string} code
   */
  async #applyDiscountCode(code) {
    const sectionId = this.#getSectionId();
    if (!sectionId) return;

    const discountCode = code.trim();
    const existingDiscounts = this.#existingDiscounts();
    if (existingDiscounts.includes(discountCode)) return;

    try {
      const data = await this.#updateCartDiscounts([...existingDiscounts, discountCode], sectionId);
      if (!data) {
        this.#showError('discount_or_club');
        return;
      }

      if (this.#hasInvalidDiscount(data.discount_codes, discountCode)) {
        this.#clearInput();
        this.#showError('discount_or_club');
        return;
      }

      if (this.#hasShippingDiscountError(data, discountCode, existingDiscounts, sectionId)) {
        this.#clearInput();
        this.#showError('shipping');
        return;
      }

      this.#clearInput();
      this.#renderUpdatedCart(data, sectionId, true);
    } catch (_error) {
      this.#showError('discount_or_club');
    }
  }

  /**
   * @param {string} discountCode
   */
  async #removeDiscountCode(discountCode) {
    const sectionId = this.#getSectionId();
    if (!sectionId) return;

    const isClubDiscount = this.#isClubDiscount(discountCode);
    const nextDiscounts = this.#existingDiscounts().filter((code) => code !== discountCode);

    try {
      if (isClubDiscount) {
        const data = await this.#updateCartDiscounts(nextDiscounts, sectionId);
        if (!data) return;

        await this.#clearClubState();
        return;
      }

      const data = await this.#updateCartDiscounts(nextDiscounts, sectionId);
      if (!data) return;

      this.#hideError();
      this.#renderUpdatedCart(data, sectionId, nextDiscounts.length > 0);
    } catch (error) {
      console.error('combine-discount-club: discount remove error', error);
    }
  }

  /**
   * @param {any} clubData
   */
  async #applyClubState(clubData) {
    const sectionId = this.#getSectionId();
    if (!sectionId || !clubData?.club_id) return;

    const hasPharmacyPickup = clubData.pharmacy_pickup_payment === true;
    // Pharmacy pickup clubs change checkout behavior, so no cart discount code is applied.
    const discounts = hasPharmacyPickup ? [] : [clubData.club_id];

    await updateOrderAttributes(sectionId, {
      club_id: clubData.club_id,
      pharmacy_pickup_payment: String(hasPharmacyPickup),
      discount_amount_percentage: String(clubData.discount_amount || ''),
    });

    this.#toggleClubCheckoutState(hasPharmacyPickup);
    this.#storeClubData(clubData);
    await this.#applyClubDiscounts(discounts, sectionId);

    document.dispatchEvent(new CustomEvent(EVENTS.CLUB_VALIDATED, {
      detail: { clubData },
      bubbles: true,
    }));

    document.dispatchEvent(new CustomEvent(EVENTS.CLUB_ACCESS_UPDATED, {
      detail: { hasAccess: true },
      bubbles: true,
    }));
  }

  async #clearClubState() {
    const sectionId = this.#getSectionId();
    if (!sectionId) return;

    await updateOrderAttributes(
      sectionId,
      {
        club_id: '',
        pharmacy_pickup_payment: '',
        discount_amount_percentage: '',
      },
      [],
      true
    );

    this.#toggleClubCheckoutState(false);
    this.#clearClubStorage();

    document.dispatchEvent(new CustomEvent(EVENTS.CLUB_ACCESS_UPDATED, {
      detail: { hasAccess: false },
      bubbles: true,
    }));

    this.#hideClubStatus();
  }

  /**
   * @param {string} value
   * @returns {Promise<any | null>}
   */
  async #fetchClubValidation(value) {
    const url = buildClubApiUrl(value, API_ENDPOINTS.GET_CLUBS);

    try {
      const response = await fetch(url);
      if (!response.ok) {
        return null;
      }

      return response.json();
    } catch (_error) {
      return null;
    }
  }

  /**
   * @param {string[]} discounts
   * @param {string} sectionId
   * @returns {Promise<any | null>}
   */
  async #updateCartDiscounts(discounts, sectionId) {
    const config = fetchConfig('json', {
      body: JSON.stringify({
        discount: discounts.join(','),
        sections: [sectionId],
      }),
    });

    const response = await fetch(Theme.routes.cart_update_url, config);
    return response.json();
  }

  /**
   * @param {string[]} discounts
   * @param {string} sectionId
   */
  async #applyClubDiscounts(discounts, sectionId) {
    const data = await this.#updateCartDiscounts(discounts, sectionId);
    if (!data) return;

    this.#renderUpdatedCart(data, sectionId, true);
  }

  #getCheckoutElements() {
    const cartItemsComponent = this.closest(
      /** @type {string} */ (CLUB_VALIDATION_SELECTORS.cartItemsComponent)
    );
    const ctasContainer = cartItemsComponent?.querySelector(
      /** @type {string} */ (CLUB_VALIDATION_SELECTORS.ctasContainer)
    );
    const defaultCheckoutButton = ctasContainer?.querySelector(
      /** @type {string} */ (CLUB_VALIDATION_SELECTORS.defaultCheckoutButton)
    );
    const customCheckoutButton = ctasContainer?.querySelector(
      /** @type {string} */ (CLUB_VALIDATION_SELECTORS.customCheckoutButton)
    );
    const cartPage = this.closest('.cart-page');

    return {
      cartItemsComponent: cartItemsComponent instanceof HTMLElement ? cartItemsComponent : null,
      defaultCheckoutButton: defaultCheckoutButton instanceof HTMLButtonElement ? defaultCheckoutButton : null,
      customCheckoutButton: customCheckoutButton instanceof HTMLButtonElement ? customCheckoutButton : null,
      cartPage: cartPage instanceof HTMLElement ? cartPage : null,
    };
  }

  /**
   * @param {boolean} showCustomCheckout
   */
  #toggleClubCheckoutState(showCustomCheckout) {
    const { defaultCheckoutButton, customCheckoutButton, cartPage } = this.#getCheckoutElements();

    toggleCheckoutButtons(
      defaultCheckoutButton,
      customCheckoutButton,
      showCustomCheckout,
      CSS_CLASSES.HIDDEN
    );

    if (customCheckoutButton instanceof HTMLButtonElement) {
      if (showCustomCheckout && this.dataset.redirectUrl) {
        customCheckoutButton.dataset.redirectUrl = this.dataset.redirectUrl;
        customCheckoutButton.onclick = () => {
          if (customCheckoutButton.dataset.redirectUrl) {
            window.location.href = customCheckoutButton.dataset.redirectUrl;
          }
        };
      } else {
        delete customCheckoutButton.dataset.redirectUrl;
        customCheckoutButton.onclick = null;
      }
    }

    if (cartPage instanceof HTMLElement) {
      cartPage.classList.toggle(CART_PAGE_CLUB_CLASS, showCustomCheckout);
    }
  }

  /**
   * @param {boolean} isDisabled
   */
  #setCheckoutButtonsDisabled(isDisabled) {
    const { defaultCheckoutButton, customCheckoutButton, cartItemsComponent } = this.#getCheckoutElements();

    if (isDisabled) {
      disableCheckoutButtons(defaultCheckoutButton, customCheckoutButton);
      return;
    }

    enableCheckoutButtons(defaultCheckoutButton, customCheckoutButton, cartItemsComponent);
  }

  /**
   * @param {any[] | undefined} discountCodes
   * @param {string} code
   * @returns {boolean}
   */
  #hasInvalidDiscount(discountCodes, code) {
    if (!Array.isArray(discountCodes)) return false;

    return discountCodes.some((/** @type {{ code: string; applicable: boolean }} */ discount) => {
      return discount.code === code && discount.applicable === false;
    });
  }

  /**
   * @param {any} data
   * @param {string} code
   * @param {string[]} existingDiscounts
   * @param {string} sectionId
   * @returns {boolean}
   */
  #hasShippingDiscountError(data, code, existingDiscounts, sectionId) {
    const newHtml = data.sections?.[sectionId];
    const parsedHtml = typeof newHtml === 'string'
      ? new DOMParser().parseFromString(newHtml, 'text/html')
      : null;
    const section = parsedHtml?.getElementById(`shopify-section-${sectionId}`);
    const discountPills = section?.querySelectorAll('.cart-discount__pill') || [];

    if (!section || !Array.isArray(data.discount_codes)) {
      return false;
    }

    const codes = Array.from(discountPills)
      .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
      .filter(Boolean);

    return (
      codes.length === existingDiscounts.length
      && codes.every((/** @type {string} */ existingCode) => existingDiscounts.includes(existingCode))
      && data.discount_codes.some((/** @type {{ code: string; applicable: boolean }} */ discount) => {
        return discount.code === code && discount.applicable === true;
      })
    );
  }

  /**
   * @param {any} data
   * @param {string} sectionId
   * @param {boolean} shouldOpenDisclosure
   */
  #renderUpdatedCart(data, sectionId, shouldOpenDisclosure) {
    const newHtml = data.sections?.[sectionId];

    if (typeof newHtml === 'string') {
      morphSection(sectionId, newHtml);
      this.#syncRenderedDisclosure(sectionId, shouldOpenDisclosure);
    }

    document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
  }

  /**
   * @param {string} sectionId
   * @param {boolean} shouldOpenDisclosure
   */
  #syncRenderedDisclosure(sectionId, shouldOpenDisclosure) {
    const sync = () => {
      const section = document.getElementById(`shopify-section-${sectionId}`);
      if (!(section instanceof HTMLElement)) return;

      const component = section.querySelector('combine-discount-club');
      if (!(component instanceof HTMLElement)) return;

      const details = component.querySelector('.combine-discount-club__details');
      if (!(details instanceof HTMLDetailsElement)) return;

      details.open = shouldOpenDisclosure;
    };

    sync();
    window.requestAnimationFrame(sync);
    window.setTimeout(sync, 80);
    window.setTimeout(sync, 200);
  }

  /**
   * @returns {string | null}
   */
  #getSectionId() {
    return typeof this.dataset.sectionId === 'string' ? this.dataset.sectionId : null;
  }

  /**
   * @returns {string}
   */
  #getInputValue() {
    const input = this.querySelector('input[name="discount_or_club"]');
    return input instanceof HTMLInputElement ? input.value.trim() : '';
  }

  #beginValidation() {
    this.#setLoading(true);
    this.#hideError();
    this.#hideClubStatus();
    this.#openDisclosure();
    this.#setCheckoutButtonsDisabled(true);
  }

  #finishValidation() {
    this.#setLoading(false);
    this.#setCheckoutButtonsDisabled(false);
  }

  /**
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('[data-combine-discount-codes] .cart-discount__pill');

    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }

  /**
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    const button = this.querySelector('[data-combine-validate-btn]');
    const buttonText = this.querySelector('[data-combine-validate-btn-text]');
    const spinner = this.querySelector('[data-combine-validate-btn-spinner]');

    if (button instanceof HTMLButtonElement) {
      button.disabled = isLoading;
    }

    if (buttonText instanceof HTMLElement) {
      if (!buttonText.dataset.defaultText) {
        buttonText.dataset.defaultText = buttonText.textContent?.trim() || 'Validate';
      }

      buttonText.textContent = isLoading ? 'Validating...' : buttonText.dataset.defaultText;
    }

    if (spinner instanceof HTMLElement) {
      spinner.hidden = !isLoading;
    }
  }

  #clearInput() {
    const input = this.querySelector('input[name="discount_or_club"]');
    if (!(input instanceof HTMLInputElement)) return;
    input.value = '';
  }

  #openDisclosure() {
    const details = this.querySelector('.combine-discount-club__details');
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = true;
  }

  #restoreClubStatus() {
    const hasActiveClub = this.dataset.hasActiveClub === 'true';
    const activeClubId = this.dataset.activeClubId || '';
    const storedClubData = getStoredClubData();
    if (!hasActiveClub || !activeClubId) {
      this.#clearClubStorage();
      return;
    }

    if (storedClubData?.club_id !== activeClubId) {
      return;
    }

    this.#showClubSuccess(storedClubData);
  }

  /**
   * @param {string} discountCode
   * @returns {boolean}
   */
  #isClubDiscount(discountCode) {
    const storedClubData = getStoredClubData();
    return storedClubData?.club_id === discountCode;
  }

  /**
   * @param {any} clubData
   */
  #storeClubData(clubData) {
    if (typeof sessionStorage === 'undefined' || !clubData?.club_id) {
      return;
    }

    sessionStorage.setItem(STORAGE_KEYS.CLUB_DATA, JSON.stringify(clubData));
  }

  #clearClubStorage() {
    if (typeof sessionStorage === 'undefined') {
      return;
    }

    sessionStorage.removeItem(STORAGE_KEYS.CLUB_DATA);
    sessionStorage.removeItem(STORAGE_KEYS.PHARMACY_DATA);
  }

  /**
   * @param {{ discount_amount?: number | string } | null | undefined} clubData
   */
  #showClubSuccess(clubData) {
    const successMessage = this.querySelector('[data-combine-club-success]');
    const errorMessage = this.querySelector('[data-combine-club-error]');
    const extraMessage = this.querySelector('[data-combine-club-extra]');
    const discountMessage = formatDiscountMessage(Number(clubData?.discount_amount || 0));

    if (successMessage instanceof HTMLElement) {
      successMessage.classList.remove('hidden');
    }

    if (errorMessage instanceof HTMLElement) {
      errorMessage.classList.add('hidden');
    }

    if (extraMessage instanceof HTMLElement) {
      if (discountMessage) {
        extraMessage.textContent = discountMessage;
        extraMessage.classList.remove('hidden');
      } else {
        extraMessage.textContent = '';
        extraMessage.classList.add('hidden');
      }
    }
  }

  #hideClubStatus() {
    const successMessage = this.querySelector('[data-combine-club-success]');
    const errorMessage = this.querySelector('[data-combine-club-error]');
    const extraMessage = this.querySelector('[data-combine-club-extra]');

    if (successMessage instanceof HTMLElement) {
      successMessage.classList.add('hidden');
    }

    if (errorMessage instanceof HTMLElement) {
      errorMessage.classList.add('hidden');
    }

    if (extraMessage instanceof HTMLElement) {
      extraMessage.textContent = '';
      extraMessage.classList.add('hidden');
    }
  }

  #hideError() {
    const errorContainer = this.querySelector('[data-combine-discount-error]');
    const discountError = this.querySelector('[data-combine-discount-error-discount]');
    const combinedError = this.querySelector('[data-combine-discount-error-discount-or-club]');
    const shippingError = this.querySelector('[data-combine-discount-error-shipping]');

    if (errorContainer instanceof HTMLElement) {
      errorContainer.classList.add('hidden');
    }

    if (discountError instanceof HTMLElement) {
      discountError.classList.add('hidden');
    }

    if (combinedError instanceof HTMLElement) {
      combinedError.classList.add('hidden');
    }

    if (shippingError instanceof HTMLElement) {
      shippingError.classList.add('hidden');
    }
  }

  /**
   * @param {'discount_code' | 'discount_or_club' | 'shipping'} type
   */
  #showError(type) {
    const errorContainer = this.querySelector('[data-combine-discount-error]');
    const discountError = this.querySelector('[data-combine-discount-error-discount]');
    const combinedError = this.querySelector('[data-combine-discount-error-discount-or-club]');
    const shippingError = this.querySelector('[data-combine-discount-error-shipping]');

    if (errorContainer instanceof HTMLElement) {
      errorContainer.classList.remove('hidden');
    }

    if (discountError instanceof HTMLElement) {
      discountError.classList.toggle('hidden', type !== 'discount_code');
    }

    if (combinedError instanceof HTMLElement) {
      combinedError.classList.toggle('hidden', type !== 'discount_or_club');
    }

    if (shippingError instanceof HTMLElement) {
      shippingError.classList.toggle('hidden', type !== 'shipping');
    }

    this.#openDisclosure();
  }
}

if (!customElements.get('combine-discount-club')) {
  customElements.define('combine-discount-club', CombineDiscountClubComponent);
}
